import Table from '@/components/Table/Table';
import { LowRatingAnswer, ReviewMember } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { BoldText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import SvgIcon from '@/components/SvgIcon';
import ModalMemberReview from './ModalMemberReview';
import { overlay } from 'overlay-kit';

type Props = {
    lowRatingAnswers?: LowRatingAnswer[];
};

const columnHelper = createColumnHelper<LowRatingAnswer>();

function FeedInstructorReviewsLowRatingAnswers({ lowRatingAnswers = [] }: Props) {
    const totalCount = lowRatingAnswers.length;

    const columns = useMemo(
        () => [
            columnHelper.accessor('memberAlias', {
                id: 'memberAlias',
                header: '회원',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue() || '-'}
                    </RegularText>
                ),
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachName', {
                id: 'coachName',
                header: '담당강사',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue() || '-'}
                    </RegularText>
                ),
                size: 124,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('answerRating', {
                id: 'answerRating',
                header: ({ column }) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                점수
                            </RegularText>
                        </div>
                    );
                },
                cell: (info) => {
                    const rating = info.getValue();
                    const starCount = Math.max(0, Math.min(Math.floor(rating), 5));
                    return (
                        <Row className="flex items-center gap-[5px]">
                            <Row className="flex items-center ">
                                {Array.from({ length: starCount }).map((_, index) => (
                                    <svg
                                        key={`star-${index}`}
                                        className="w-[14px] h-[14px] text-[#3D73DD]"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </Row>
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                {rating}
                            </RegularText>
                        </Row>
                    );
                },
                size: 120,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('reviewStage', {
                id: 'reviewStage',
                header: '리뷰 회차',
                cell: (info) => {
                    const stage = info.getValue() || '';
                    const normalized = stage.toLowerCase();
                    const stageLabel = normalized.includes('first')
                        ? '첫'
                        : normalized.includes('middle')
                          ? '중간'
                          : normalized.includes('final')
                            ? '마지막'
                            : stage || '-';
                    return (
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                            {stageLabel}
                        </RegularText>
                    );
                },
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('questionSubject', {
                id: 'questionSubject',
                header: '리뷰 항목',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue() || '-'}
                    </RegularText>
                ),
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('answerContent', {
                id: 'answerContent',
                header: '상세 리뷰',
                cell: (info) => (
                    <Row className="flex flex-row w-[269px] px-[12px]">
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1} className="truncate w-full">
                            {info.getValue() || '-'}
                        </RegularText>
                    </Row>
                ),
                size: 269,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('reviewCreatedAt', {
                id: 'reviewCreatedAt',
                header: ({ column }) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                작성 일시
                            </RegularText>
                        </div>
                    );
                },
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue() ? dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm') : '-'}
                    </RegularText>
                ),
                size: 269,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
        ],
        [],
    );

    return (
        <div className="flex w-full flex-col bg-[#FFFFFF] border-[0.5px] border-[#E8E8E8] px-[29px] pt-[38px]  rounded-[25px]">
            <div className="mb-[18px]">
                <Row className="flex items-center mb-[36px]">
                    <RegularText fontSize={20} lineHeight={26} color={'#191F28'}>
                        3점 이하 리뷰 / 총 {totalCount.toLocaleString('ko-KR')}개
                    </RegularText>
                </Row>
            </div>
            <Table
                data={lowRatingAnswers}
                columns={columns}
                rowHeight={84}
                headerColor="#FFFFFF"
                isUnballancedRowColor
                rowRadius={14}
                emptyComponent={
                    <RegularText fontSize={20} lineHeight={30} color={colors.Text_1} letterSpacing={-0.3}>
                        등록된 회원 리뷰가 없어요🤔
                    </RegularText>
                }
                onRowClick={(row: ReviewMember) => {
                    // TODO : 나중에 reviewId 로 바꿔놓기
                    overlay.open(({ unmount }) => (
                        <ModalMemberReview unmount={unmount} reviewId={row.reviewId || ''} />
                    ));
                }}
                isPagination
                customPaginationComponent={({
                    nextPage,
                    canNextPage,
                    previousPage,
                    canPreviousPage,
                    pageIndex,
                    totalPageIndex,
                    setPageIndex,
                }) => {
                    const totalPages = totalPageIndex || 0;
                    const currentPage = (pageIndex || 0) + 1;

                    // Calculate visible page range (groups of 5: 1-5, 6-10, 11-15, etc.)
                    const pageGroup = Math.ceil(currentPage / 5);
                    const startPage = (pageGroup - 1) * 5 + 1;
                    const endPage = Math.min(pageGroup * 5, totalPages);

                    const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

                    return (
                        <Row className="flex items-center  self-center my-[22px] overflow-auto">
                            <Row className="flex items-center gap-[12px] mr-[30px]">
                                {visiblePages.map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        onClick={() => setPageIndex?.(pageNum - 1)}
                                        className={`w-[32px] h-[32px] rounded-full flex items-center justify-center ${
                                            currentPage === pageNum ? 'bg-[#3D4244]' : 'hover:bg-[#F0F0F0]'
                                        }`}
                                    >
                                        <MediumText
                                            fontSize={16}
                                            lineHeight={21}
                                            color={currentPage === pageNum ? '#FFFFFF' : colors.Text_1}
                                        >
                                            {pageNum}
                                        </MediumText>
                                    </button>
                                ))}
                            </Row>
                            <button
                                onClick={() => setPageIndex?.(Math.max(0, startPage - 6))}
                                disabled={startPage === 1}
                                className="disabled:opacity-30 disabled:cursor-not-allowed mr-[37px]"
                            >
                                <SvgIcon
                                    name={'SvgLeftStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>

                            <button
                                onClick={() => setPageIndex?.(Math.min(totalPages - 1, endPage))}
                                disabled={endPage >= totalPages}
                                className="disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <SvgIcon
                                    name={'SvgRightStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    );
                }}
            />
        </div>
    );
}

export default FeedInstructorReviewsLowRatingAnswers;
